import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';

import Busy from '../../components/Busy';
import { useAPIAuthenticatedService } from '../../helpers/APICallsAuthenticated';
import { AdminActivityRecord, AdminActivityUserSummary, CacheKeys } from '../../helpers/DataStructures';
import DateTimeFormat from '../../helpers/DateTimeFormat';
import ReferralRouteShell from './ReferralRouteShell';

interface AdminActivityPageProps {
  groupId: string;
  title: string;
}

type ActivityFilter = 'all' | 'sessionStarts';

const getUserLabel = (user?: AdminActivityUserSummary | null) => {
  if (!user) {
    return 'Unknown user';
  }

  const personName = `${user.givenName} ${user.familyName}`.trim();
  return user.companyName || personName || user.email;
};

const getActivitySubject = (activity: AdminActivityRecord) => {
  if (activity.kind === 'request') {
    return [activity.requestMethod, activity.requestPath].filter(Boolean).join(' ');
  }

  return [activity.entityType, activity.entityId].filter(Boolean).join(' ');
};

const getActivityTime = (created: string) => {
  const date = new Date(created);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
};

const AdminActivityPage: React.FC<AdminActivityPageProps> = ({ groupId, title }) => {
  const apiServiceInstance = useAPIAuthenticatedService();
  const [activityFilter, setActivityFilter] = React.useState<ActivityFilter>('all');

  const { isLoading, isError, data, error } = useQuery(
    [CacheKeys.ADMIN_ACTIVITY, groupId, activityFilter],
    () =>
      activityFilter === 'sessionStarts'
        ? apiServiceInstance.getAdminActivity(groupId, {
            kind: 'request',
            eventType: 'activity_session_started',
          })
        : apiServiceInstance.getAdminActivity(groupId),
    {
      keepPreviousData: true,
      retry: false,
    }
  );

  const resolvedErrorMessage =
    error instanceof Error && error.message ? error.message : 'Unable to load admin activity.';

  return (
    // REQ-029: group activity pages use the same wide hidden-admin shell as admin referral tables.
    <ReferralRouteShell backTo="/admins" fitContent maxWidth={false} title={title}>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button
          size="small"
          variant={activityFilter === 'all' ? 'contained' : 'outlined'}
          onClick={() => setActivityFilter('all')}
        >
          All activity
        </Button>
        <Button
          size="small"
          startIcon={<FilterListIcon fontSize="small" />}
          variant={activityFilter === 'sessionStarts' ? 'contained' : 'outlined'}
          onClick={() => setActivityFilter('sessionStarts')}
        >
          Session starts
        </Button>
      </Stack>
      {isLoading ? (
        <Busy message="Loading activity..." />
      ) : isError ? (
        <Typography>{resolvedErrorMessage}</Typography>
      ) : !data || data.length === 0 ? (
        <Typography>No activity found for group {groupId}.</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'visible' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Kind</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Summary</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((activity) => {
                const actor = activity.actorUser ?? activity.user;

                return (
                  <TableRow key={`${activity.kind}-${activity.id}`}>
                    <TableCell>{DateTimeFormat(activity.created)}</TableCell>
                    <TableCell>{getActivityTime(activity.created)}</TableCell>
                    <TableCell>{activity.kind}</TableCell>
                    <TableCell>{getUserLabel(actor)}</TableCell>
                    <TableCell>{activity.eventType}</TableCell>
                    <TableCell>{getActivitySubject(activity)}</TableCell>
                    <TableCell>{activity.summary || activity.sessionStartReason || ''}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </ReferralRouteShell>
  );
};

export default AdminActivityPage;
